'use client';

import { MESSAGE_TYPES } from '@repo/utils';
import type { ChatDocument, Message } from '@repo/api';
import { cn } from '@/lib/cn';
import { DocumentPreview } from './DocumentPreview';

interface DocumentViewCardProps {
  message: Message;
}

/** A single document and a list are both accepted while the contract settles. */
function toDocuments(contentData: unknown): ChatDocument[] {
  if (!contentData) return [];
  const list = Array.isArray(contentData) ? contentData : [contentData];
  return list.filter((doc): doc is ChatDocument => !!doc && typeof doc === 'object');
}

/**
 * A file already stored on the chat, shown read-only — the counterpart to
 * DocumentRequestCard, which asks for one. Aligned like a bubble: the renter's
 * own uploads sit on the right, anything the backend attaches on the left.
 */
export function DocumentViewCard({ message }: DocumentViewCardProps) {
  const documents = toDocuments(message.contentData);
  if (documents.length === 0) return null;

  const isMe = message.senderType === MESSAGE_TYPES.USER;

  return (
    <div className={cn('flex flex-col gap-3', isMe ? 'items-end pl-21' : 'ml-10 pr-12')}>
      {documents.map((doc, index) => (
        <DocumentPreview
          key={doc.url ?? `${doc.name}-${index}`}
          name={doc.name || 'Document'}
          {...(doc.url ? { url: doc.url } : {})}
          {...(doc.mimeType ? { mimeType: doc.mimeType } : {})}
        />
      ))}
    </div>
  );
}

export default DocumentViewCard;
